/**
 * v1.3.0 — HACCP Libro Unico Ingredienti.
 *  - UL carica schede tecniche fornitori (PDF)
 *  - Consulente HACCP/admin genera in 1 click il "Libro Unico degli Ingredienti"
 *  - Libri generati restano salvati e scaricabili dalla UL
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { BookOpen, Upload, Download, Trash2, Loader2, FileText, Info, ChefHat } from 'lucide-react';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';

export default function LibroIngredientiPage() {
  // v1.3.10 — UL selezionata dal context globale della sidebar
  const { currentPdvId, setCurrentPdvId } = useCurrentPdv();
  const selectedPdv = currentPdvId || '';
  // Wrapper protettivo (vedi SchedeProdottoPage per dettagli)
  const setSelectedPdv = (id) => { if (id) setCurrentPdvId(id); };

  const [me, setMe] = useState(null);
  const [pdvList, setPdvList] = useState([]);
  const [schede, setSchede] = useState([]);
  const [libri, setLibri] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ nome_prodotto: '', fornitore: '', note: '', file: null });

  const isAdmin = me && ['super_admin', 'admin', 'amministratore', 'consulente_haccp', 'haccp_admin'].includes(me.role);

  useEffect(() => {
    api.get('/auth/me').then((r) => setMe(r.data)).catch(() => {});
    api.get('/pdv').then((r) => {
      const ls = r.data || [];
      setPdvList(ls);
      // v1.3.5 — NON auto-seleziona: la scelta UL deve essere esplicita
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedPdv) refresh();
    else { setSchede([]); setLibri([]); }
  }, [selectedPdv]);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/haccp/libro-ingredienti/schede?pdv_id=${selectedPdv}`),
        api.get(`/haccp/libro-ingredienti/libri?pdv_id=${selectedPdv}`),
      ]);
      setSchede(s.data || []);
      setLibri(l.data || []);
    } catch (e) {
      toast.error('Errore caricamento dati');
    }
  };

  const handleUpload = async () => {
    if (!form.file || !form.nome_prodotto.trim()) {
      toast.error('Inserisci nome prodotto e seleziona un PDF');
      return;
    }
    if (!selectedPdv) {
      toast.error('Seleziona una UL');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdv_id', selectedPdv);
      fd.append('nome_prodotto', form.nome_prodotto);
      fd.append('fornitore', form.fornitore || '');
      fd.append('note', form.note || '');
      fd.append('file', form.file);
      await api.post('/haccp/libro-ingredienti/schede-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Scheda caricata');
      setForm({ nome_prodotto: '', fornitore: '', note: '', file: null });
      // reset file input
      const fi = document.getElementById('scheda-file');
      if (fi) fi.value = '';
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload fallito');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Eliminare definitivamente questa scheda?')) return;
    try {
      await api.delete(`/haccp/libro-ingredienti/schede/${id}`);
      toast.success('Scheda eliminata');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  const handleDownloadScheda = async (s) => {
    try {
      const r = await api.get(`/haccp/libro-ingredienti/schede/${s.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = s.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fallito'); }
  };

  const handleDownloadLibro = async (l) => {
    try {
      const r = await api.get(`/haccp/libro-ingredienti/libri/${l.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = l.filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download fallito'); }
  };

  const handleGeneraLibro = async () => {
    if (!selectedPdv) { toast.error('Seleziona una UL'); return; }
    if (schede.length === 0) { toast.error('Carica almeno una scheda prima di generare il libro'); return; }
    setGenerating(true);
    try {
      const r = await api.post(`/haccp/libro-ingredienti/genera?pdv_id=${selectedPdv}`, {}, { responseType: 'blob' });
      // Download automatico
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Libro_Unico_Ingredienti_${selectedPdv}.docx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Libro Unico Ingredienti generato e salvato nei file della UL');
      await refresh();
    } catch (e) {
      const blob = e.response?.data;
      let msg = e.response?.data?.detail || 'Generazione fallita';
      if (blob && blob.text) {
        try { const txt = await blob.text(); msg = JSON.parse(txt).detail || msg; } catch {}
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl" data-testid="libro-ingredienti-page">
      <div className="flex items-center gap-3">
        <div className="bg-orange-100 p-2 rounded-lg">
          <BookOpen className="w-6 h-6 text-orange-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Libro Unico degli Ingredienti</h1>
          <p className="text-sm text-slate-500">
            L&apos;Unità Locale carica le schede tecniche dei fornitori in PDF. Il Consulente HACCP genera in 1 click il Libro Unico riepilogativo che resta a disposizione della UL.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-start gap-2">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Ruoli:</strong> ogni UL può caricare/eliminare le proprie schede. Solo il <strong>Consulente HACCP</strong> (o admin) può generare il Libro Unico, che viene salvato automaticamente nei file della UL e reso disponibile sotto il tab &quot;Libri generati&quot;.
        </div>
      </div>

      {/* SELEZIONE UL */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Unità Locale</Label>
              <Select value={selectedPdv} onValueChange={setSelectedPdv}>
                <SelectTrigger data-testid="pdv-select"><SelectValue placeholder="Seleziona UL..." /></SelectTrigger>
                <SelectContent>
                  {pdvList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name || p.denominazione}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="h-9 px-3">
              {schede.length} scheda{schede.length !== 1 ? 'e' : ''} caricata{schede.length !== 1 ? 'e' : ''}
            </Badge>
            {isAdmin && (
              <Button
                onClick={handleGeneraLibro}
                disabled={generating || schede.length === 0}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="genera-libro-btn"
              >
                {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generazione...</> : <><ChefHat className="w-4 h-4 mr-1" /> Genera Libro Unico</>}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* UPLOAD */}
      {!selectedPdv ? (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-10 text-center text-slate-500">
            <Info className="w-10 h-10 mx-auto mb-3 text-amber-500" />
            <p className="text-base font-medium">Seleziona un&apos;Unità Locale per continuare</p>
            <p className="text-sm mt-1">Le schede tecniche e il Libro Unico Ingredienti sono visibili e modificabili SOLO dopo aver scelto l&apos;Unità Locale di riferimento.</p>
          </CardContent>
        </Card>
      ) : (
        <>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-600" /> Carica nuova scheda tecnica (PDF fornitore)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Nome prodotto/ingrediente *</Label>
              <Input value={form.nome_prodotto} onChange={(e) => setForm({...form, nome_prodotto: e.target.value})} placeholder="Es. Farina 00" data-testid="nome-prodotto-input" />
            </div>
            <div>
              <Label className="text-xs">Fornitore</Label>
              <Input value={form.fornitore} onChange={(e) => setForm({...form, fornitore: e.target.value})} placeholder="Es. Molini Spadoni" data-testid="fornitore-input" />
            </div>
            <div>
              <Label className="text-xs">Allergeni / Note</Label>
              <Input value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} placeholder="Glutine, frumento..." data-testid="note-input" />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">File PDF *</Label>
              <Input id="scheda-file" type="file" accept="application/pdf" onChange={(e) => setForm({...form, file: e.target.files?.[0] || null})} data-testid="file-input" />
            </div>
            <Button onClick={handleUpload} disabled={uploading || !form.file || !form.nome_prodotto.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="upload-btn">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Caricamento...</> : <><Upload className="w-4 h-4 mr-1" /> Carica</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SCHEDE CARICATE */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Schede tecniche caricate ({schede.length})</CardTitle></CardHeader>
        <CardContent>
          {schede.length === 0 ? (
            <p className="text-sm text-slate-400 italic">Nessuna scheda caricata per questa UL.</p>
          ) : (
            <div className="space-y-2">
              {schede.map((s) => (
                <div key={s.id} className="flex items-center gap-2 p-2 border border-slate-200 rounded hover:bg-slate-50">
                  <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.nome_prodotto}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {s.fornitore || '—'} {s.note ? `· ${s.note}` : ''} · caricato il {new Date(s.caricato_il).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDownloadScheda(s)} data-testid={`dl-scheda-${s.id}`}>
                    <Download className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(s.id)} className="text-red-600 hover:bg-red-50" data-testid={`del-scheda-${s.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* LIBRI GENERATI */}
      {libri.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-orange-600" /> Libri Unici Ingredienti generati ({libri.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {libri.map((l) => (
                <div key={l.id} className="flex items-center gap-2 p-2 border border-orange-200 bg-orange-50 rounded">
                  <BookOpen className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{l.filename}</div>
                    <div className="text-xs text-orange-800 truncate">
                      Generato il {new Date(l.generato_il).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} da {l.generato_da} · {l.schede_count} schede
                    </div>
                  </div>
                  <Button size="sm" onClick={() => handleDownloadLibro(l)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`dl-libro-${l.id}`}>
                    <Download className="w-3 h-3 mr-1" /> Scarica DOCX
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}
    </div>
  );
}
